import { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);

  useEffect(() => {
    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    
    if (isStandalone) {
      return; // Already installed, don't show prompt
    }

    // Show iOS prompt
    if (isIOSDevice) {
      const dismissed = localStorage.getItem('pwa-ios-dismissed');
      if (!dismissed) {
        setTimeout(() => setShowIOSPrompt(true), 3000);
      }
    }

    // Listen for install prompt (Android/Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      const dismissed = localStorage.getItem('pwa-dismissed');
      if (!dismissed) {
        setTimeout(() => setShowInstall(true), 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowInstall(false);
    }
    setDeferredPrompt(null);
  };

  const dismissPrompt = () => {
    setShowInstall(false);
    setShowIOSPrompt(false);
    localStorage.setItem('pwa-dismissed', 'true');
    localStorage.setItem('pwa-ios-dismissed', 'true');
  };

  // iOS Install Instructions
  if (showIOSPrompt && isIOS) {
    return (
      <div className="fixed bottom-20 left-4 right-4 z-50 animate-fade-in-up">
        <div className="bg-card border border-primary/30 p-4 rounded-lg shadow-2xl">
          <button 
            onClick={dismissPrompt}
            className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-start gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_intimate-apparel-19/artifacts/rhgc7x73_IMG-20260216-WA0015.jpg" 
              alt="Indy Elegance" 
              className="w-12 h-12 rounded-lg"
            />
            <div className="flex-1">
              <h3 className="font-bold text-sm mb-1">Instalar Indy Elegance</h3>
              <p className="text-xs text-muted-foreground mb-2">
                Adicione o app à sua tela inicial:
              </p>
              <ol className="text-xs text-muted-foreground space-y-1">
                <li>1. Toque em <span className="text-primary">Compartilhar</span> (ícone de quadrado com seta)</li>
                <li>2. Role e toque em <span className="text-primary">"Adicionar à Tela de Início"</span></li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Android/Chrome Install Prompt
  if (!showInstall || !deferredPrompt) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 animate-fade-in-up">
      <div className="bg-card border border-primary/30 p-4 rounded-lg shadow-2xl">
        <button 
          onClick={dismissPrompt}
          className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-foreground"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-4">
          <img 
            src="https://customer-assets.emergentagent.com/job_intimate-apparel-19/artifacts/rhgc7x73_IMG-20260216-WA0015.jpg" 
            alt="Indy Elegance" 
            className="w-14 h-14 rounded-lg"
          />
          <div className="flex-1">
            <h3 className="font-bold mb-1">Instalar App</h3>
            <p className="text-xs text-muted-foreground">
              Adicione Indy Elegance à sua tela inicial para acesso rápido!
            </p>
          </div>
          <Button 
            onClick={handleInstall}
            className="btn-primary px-4 py-2"
          >
            <Download className="w-4 h-4 mr-1" />
            Instalar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InstallPWA;
